import {
  FileText,
  Image,
  Music,
  Video,
  Archive,
  BookOpen,
  Type,
  Database,
  Presentation,
  FileSpreadsheet,
  Package,
  Box,
  GitBranch,
  Mail,
  DollarSign,
  MapPin,
  StickyNote,
  Briefcase,
} from "lucide-react";

export interface CategoryInfo {
  name: string;
  icon: any;
  color: string;
  description: string;
  slug: string;
}

export const categoryInfo: Record<string, CategoryInfo> = {
  document: {
    name: "Documents",
    icon: FileText,
    color: "bg-blue-500",
    description: "Text documents, PDFs, and office files",
    slug: "document",
  },
  image: {
    name: "Images",
    icon: Image,
    color: "bg-green-500",
    description: "Photos, graphics, and image files",
    slug: "image",
  },
  video: {
    name: "Videos",
    icon: Video,
    color: "bg-red-500",
    description: "Movies, clips, and video files",
    slug: "video",
  },
  audio: {
    name: "Audio",
    icon: Music,
    color: "bg-purple-500",
    description: "Music, podcasts, and audio files",
    slug: "audio",
  },
  archive: {
    name: "Archives",
    icon: Archive,
    color: "bg-orange-500",
    description: "Compressed and archive files",
    slug: "archive",
  },
  ebook: {
    name: "eBooks",
    icon: BookOpen,
    color: "bg-cyan-500",
    description: "Digital books and publications",
    slug: "ebook",
  },
  font: {
    name: "Fonts",
    icon: Type,
    color: "bg-pink-500",
    description: "Font files and typography",
    slug: "font",
  },
  presentation: {
    name: "Presentations",
    icon: Presentation,
    color: "bg-indigo-500",
    description: "Slideshow and presentation files",
    slug: "presentation",
  },
  spreadsheet: {
    name: "Spreadsheets",
    icon: FileSpreadsheet,
    color: "bg-emerald-500",
    description: "Spreadsheet and calculation files",
    slug: "spreadsheet",
  },
  data: {
    name: "Data",
    icon: Database,
    color: "bg-gray-500",
    description: "Data exchange and markup files",
    slug: "data",
  },
  cad: {
    name: "CAD",
    icon: Package,
    color: "bg-yellow-500",
    description: "Computer-aided design files",
    slug: "cad",
  },
  "3d": {
    name: "3D Models",
    icon: Box,
    color: "bg-teal-500",
    description: "3D models and scenes",
    slug: "3d",
  },
  diagram: {
    name: "Diagrams",
    icon: GitBranch,
    color: "bg-violet-500",
    description: "Diagrams and flowcharts",
    slug: "diagram",
  },
  email: {
    name: "Email",
    icon: Mail,
    color: "bg-blue-600",
    description: "Email messages and mailboxes",
    slug: "email",
  },
  finance: {
    name: "Finance",
    icon: DollarSign,
    color: "bg-green-600",
    description: "Financial and business reporting",
    slug: "finance",
  },
  gis: {
    name: "GIS",
    icon: MapPin,
    color: "bg-amber-500",
    description: "Geographic information systems",
    slug: "gis",
  },
  note: {
    name: "Notes",
    icon: StickyNote,
    color: "bg-lime-500",
    description: "Note-taking applications",
    slug: "note",
  },
  project: {
    name: "Project",
    icon: Briefcase,
    color: "bg-slate-600",
    description: "Project management files",
    slug: "project",
  },
};

// Helper function to generate converter URL from category name
export const getCategoryConverterUrl = (categoryName: string): string => {
  return `/${categoryName.toLowerCase().replace(/\s+/g, "-")}-converter`;
};

// Get ordered categories for display
export const getOrderedCategories = (): CategoryInfo[] => {
  const priorityOrder = [
    "document",
    "image", 
    "video",
    "audio",
    "archive",
    "presentation",
    "spreadsheet",
    "ebook",
    "font",
    "3d",
    "cad",
    "data",
    "diagram",
    "email",
    "finance",
    "gis",
    "note",
    "project",
  ];

  return priorityOrder
    .map((key) => categoryInfo[key])
    .filter(Boolean);
};
